import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { isQueryReady } from "src/api/axiosClient/utils";
import { extendSchemaWithJsonAttributes } from "src/utils/zodDynamicSchema.ts";
import { PRODUCT, useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { OlapScoresResult, OlapScoresResultSchema } from "../olapScores/olapScoresSchema";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import { Campaign, CampaignSchema } from "../useCampaign";

export const getOneProductKey = "getOneProduct" as const;

type Options = {
    campaignId?: number;
    companyId?: number;
    scoresQuery?: OlapScoresQuery;
    enabled?: boolean;
};
export const useGetOneProduct = (productId?: string | number, options?: Options) => {
    const { entitiesJsonAttributes } = useFoodpilotContext();
    const { campaignId, companyId, enabled = true, scoresQuery } = options || {};

    const isEnabled = enabled && productId !== undefined;
    const query = useQuery({
        queryKey: [getOneProductKey, productId, companyId, campaignId, scoresQuery, enabled, "v3"],
        cacheTime: 0,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: isEnabled,
        queryFn: async () => {
            const url = `/api/products/${productId}`;
            const response = await loggedInApiCall.get<Product>(url, {
                params: {
                    campaign: campaignId,
                    organization_id: companyId,
                    ...scoresQuery,
                },
            });
            return response;
        },
        select: (request) => {
            const product = request.data;
            const finalSchema = extendSchemaWithJsonAttributes(ProductSchema, entitiesJsonAttributes, PRODUCT);

            return zodParseWithDetailedLog(product, finalSchema, {
                operation: "GET",
                endpoint: "/products/:id",
            }) as ProductWithAttributes;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        product: query.data,
        isReady,
        ...query,
    };
};

// INGREDIENTS
type IngredientLine = {
    "@id": string;
    id: number;
    name: string;
};
const IngredientLineZodSchema: z.ZodSchema<IngredientLine> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});
type SupplierFromIngSupplier = {
    "@id": string;
    id: number;
    name: string;
    clientId?: string;
};
const SupplierFromIngSupplierSchema: z.ZodSchema<SupplierFromIngSupplier> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    clientId: z.string().optional(),
});
type IngredientSupplier = {
    "@id": string;
    id: number;
    supplier: SupplierFromIngSupplier;
    quantity?: number;
    unit?: string;
};
const IngredientSupplierSchema: z.ZodSchema<IngredientSupplier> = z.object({
    "@id": z.string(),
    id: z.number(),
    supplier: SupplierFromIngSupplierSchema,
    quantity: z.number().optional(),
    unit: z.string().optional(),
});
export type Ingredient = {
    "@id": string;
    id: number;
    name: string;
    externalId?: number;
    suppliers?: IngredientSupplier[];
    ingredientReferenceId?: number;
    referenceSource?: "sure" | "foodpilot";
    ingredientLine?: IngredientLine;
    organic?: boolean;
    origin?: string;
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
};
export const IngredientZodSchema: z.ZodSchema<Ingredient> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    externalId: z.number().optional(),
    suppliers: IngredientSupplierSchema.array().optional(),
    ingredientLine: IngredientLineZodSchema.optional(),
    ingredientReferenceId: z.number().optional(),
    referenceSource: z.union([z.literal("sure"), z.literal("foodpilot")]).optional(),
    organic: z.boolean().optional(),
    origin: z.string().optional(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

// Simple type for intermediate product when used as ingredient (to avoid circular references)
export type IntermediateProductAsIngredient = {
    "@id": string;
    id: number;
    title: string;
    isIntermediate?: boolean;
};

export const IntermediateProductAsIngredientSchema: z.ZodSchema<IntermediateProductAsIngredient> = z.object({
    "@id": z.string(),
    id: z.number(),
    title: z.string(),
    isIntermediate: z.boolean().optional(),
});

export type ProductIngredientResolvedTransport = {
    stage: TransportStage;
    source: "product" | "supplier" | "default" | "none";
    circuit: {
        id: number | null;
        name?: string | null;
        originCountryCode?: string | null;
        destinationCountryCode?: string | null;
    } | null;
};

const ProductIngredientResolvedTransportSchema: z.ZodSchema<ProductIngredientResolvedTransport> = z.object({
    stage: z.literal("ingredient_to_processing_site"),
    source: z.union([z.literal("product"), z.literal("supplier"), z.literal("default"), z.literal("none")]),
    circuit: z
        .object({
            id: z.number().nullable(),
            name: z.string().nullable().optional(),
            originCountryCode: z.string().nullable().optional(),
            destinationCountryCode: z.string().nullable().optional(),
        })
        .nullable(),
});

export type ProductIngredient = {
    "@id": string;
    id: number;
    proportion?: number;
    brand?: string;
    organic?: boolean;
    quantity?: number;
    usedQuantity?: number;
    usedProportion?: number;
    isUsedQuantityEqualToQuantity: boolean;
    ciqualFoodId?: number;
    origin?: string;
    category?: string;
    agriculturalProductionDiversity?: number;
    agroEcologicalInfrastructure?: number;
    farmingConditionsQuality?: number;
    agribalyseOrganic?: number;
    agribalyseConventional?: number;
    resolvedTransports?: ProductIngredientResolvedTransport[];
} & (
    | {
          ingredient: Ingredient;
          intermediateProduct?: undefined;
      }
    | {
          intermediateProduct: IntermediateProductAsIngredient;
          ingredient?: undefined;
      }
);

export const ProductIngredientSchema: z.ZodSchema<ProductIngredient> = z.intersection(
    z.object({
        "@id": z.string(),
        id: z.number(),
        proportion: z.number().optional(),
        quantity: z.number().optional(),
        usedQuantity: z.number().optional(),
        usedProportion: z.number().optional(),
        isUsedQuantityEqualToQuantity: z.boolean(),
        organic: z.boolean().optional(),
        ciqual_food_id: z.number().optional(),
        origin: z.string().optional(),
        category: z.string().optional(),
        agricultural_production_diversity: z.number().optional(),
        agro_ecological_infrastructure: z.number().optional(),
        farming_conditions_quality: z.number().optional(),
        agribalyse_organic: z.number().optional(),
        agribalyse_conventional: z.number().optional(),
        resolvedTransports: ProductIngredientResolvedTransportSchema.array().optional(),
    }),
    z.union([
        z.object({
            ingredient: IngredientZodSchema,
            intermediateProduct: z.preprocess((v) => v ?? undefined, z.undefined()),
        }),
        z.object({
            ingredient: z.preprocess((v) => v ?? undefined, z.undefined()),
            intermediateProduct: IntermediateProductAsIngredientSchema,
        }),
    ]),
);

export type Brand = {
    "@id": IRI;
    id: number;
    name: string;
    logoUrl?: string | null;
};

export const BrandSchema: z.ZodSchema<Brand> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    logoUrl: z.string().optional(),
});

export type ProductPhoto = {
    "@id"?: IRI;
    id: string;
    objectKey: string;
    url: string;
    originalName: string;
    mimeType: string;
    size: number;
    position: number;
    isPrimary: boolean;
    alt?: string | null;
};

export const ProductPhotoSchema: z.ZodSchema<ProductPhoto> = z.object({
    "@id": z.string().optional(),
    id: z.string(),
    objectKey: z.string(),
    url: z.string(),
    originalName: z.string(),
    mimeType: z.string(),
    size: z.number(),
    position: z.number(),
    isPrimary: z.boolean(),
    alt: z.string().nullable().optional(),
});

export type ProductLine = {
    "@id": IRI;
    id: number;
    name: string;
};

export const ProductLineZodSchema: z.ZodSchema<ProductLine> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type Tag = {
    "@id": IRI;
    id: number;
    name: string;
};

export const TagSchema: z.ZodSchema<Tag> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type Packaging = {
    "@id": IRI;
    "@type"?: "PackagingElement" | "PackagingComposite";
    id: number;
    name: string;
    externalId?: string;
    weight?: number | null;
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
};
const PackagingSchema: z.ZodSchema<Packaging> = z.object({
    "@id": z.string(),
    "@type": z.enum(["PackagingElement", "PackagingComposite"]).optional(),
    id: z.number(),
    name: z.string(),
    externalId: z.string().optional(),
    weight: z.number().nullable().optional(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

export type ProductPackaging = {
    "@id": IRI;
    id: number;
    packaging?: Packaging | null;
    packagingReferenceId?: number | null;
    packagingReferenceKind?: "element" | "composite" | null;
    packagingReferenceName?: string | null;
    usedWeight?: number | null;
    quantity: number | null;
};
const ProductPackagingSchema: z.ZodSchema<ProductPackaging> = z.preprocess(
    (value) => {
        if (typeof value !== "object" || value === null || Array.isArray(value) || "quantity" in value) {
            return value;
        }

        return {
            ...value,
            quantity: null,
        };
    },
    z.object({
        "@id": z.string(),
        id: z.number(),
        packaging: PackagingSchema.nullable().optional(),
        packagingReferenceId: z.number().nullable().optional(),
        packagingReferenceKind: z.enum(["element", "composite"]).nullable().optional(),
        packagingReferenceName: z.string().nullable().optional(),
        usedWeight: z.number().nullable().optional(),
        quantity: z.number().nullable(),
    }),
);

export type ProductTransformation = {
    "@id": IRI;
    id: number;
    transformationReferenceId: number;
    transformationCode?: string | null;
    transformationNameFr?: string | null;
    massGrams?: number | null;
    organization?: {
        "@id": IRI;
        id: number;
        name: string;
    } | null;
    position?: number;
};

const ProductTransformationSchema: z.ZodSchema<ProductTransformation> = z.object({
    "@id": z.string(),
    id: z.number(),
    transformationReferenceId: z.number(),
    transformationCode: z.string().nullable().optional(),
    transformationNameFr: z.string().nullable().optional(),
    massGrams: z.number().nullable().optional(),
    organization: z
        .object({
            "@id": z.string(),
            id: z.number(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    position: z.number().optional(),
});

export type ProductOrganizationActivityData = {
    id: number;
    "@id": IRI;
    campaign: Campaign;
    quantity?: number;
};

const ProductOrganizationActivityDataSchema: z.ZodSchema<ProductOrganizationActivityData> = z.object({
    id: z.number(),
    "@id": z.string(),
    campaign: CampaignSchema,
    quantity: z.number().optional(),
});

export const ProductCompanySchema: z.ZodSchema<ProductCompany> = z.object({
    "@id": z.string(),
    id: z.number(),
    organization: z.object({
        "@id": z.string(),
        id: z.number(),
        name: z.string(),
    }),
    productOrganizationActivityDatas: z.array(ProductOrganizationActivityDataSchema),
});

export type ProductCompanyCompany = {
    "@id": IRI;
    id: number;
    name: string;
};

export const ProductCompanyCompanySchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export type ProductCompany = {
    "@id": IRI;
    id: number;
    organization: ProductCompanyCompany;
    productOrganizationActivityDatas: ProductOrganizationActivityData[];
};

export type ProductConsumption = {
    "@id": IRI;
    id: number;
    consumptionReferenceId: number;
    consumptionCode?: string | null;
    consumptionNameFr?: string | null;
};

export const ProductConsumptionSchema: z.ZodSchema<ProductConsumption> = z.object({
    "@id": z.string(),
    id: z.number(),
    consumptionReferenceId: z.number(),
    consumptionCode: z.string().nullable().optional(),
    consumptionNameFr: z.string().nullable().optional(),
});

export type TransportStage =
    | "ingredient_to_processing_site"
    | "packaging_to_processing_site"
    | "product_to_storage"
    | "storage_to_retail"
    | "retail_to_consumer"
    | "custom";

export type ProductTransport = {
    "@id": IRI;
    id: number;
    productIngredient?: { "@id": IRI; id: number } | null;
    productPackaging?: { "@id": IRI; id: number } | null;
    stage: TransportStage;
    transportCircuit: {
        "@id"?: IRI;
        id: number;
        name?: string | null;
        linkMode: "LOCAL" | "LINKED";
        sourceReferenceCircuitId?: number | null;
    };
};

const TransportLinkModeApiSchema = z
    .enum(["LOCAL", "LINKED", "local", "linked"])
    .transform((value) => value.toUpperCase() as "LOCAL" | "LINKED");

const ProductTransportCircuitSchema = z
    .object({
        "@id": z.string().optional(),
        id: z.number(),
        name: z.string().nullable().optional(),
        linkMode: z.unknown().optional(),
        sourceReferenceCircuitId: z.number().nullable().optional(),
    })
    .transform((circuit) => {
        const parsedLinkMode = TransportLinkModeApiSchema.safeParse(circuit.linkMode);

        return {
            ...circuit,
            linkMode:
                parsedLinkMode.success ? parsedLinkMode.data
                : circuit.sourceReferenceCircuitId != null ? "LINKED"
                : "LOCAL",
        };
    });

export const ProductTransportSchema: z.ZodSchema<ProductTransport> = z.object({
    "@id": z.string(),
    id: z.number(),
    productIngredient: z.object({ "@id": z.string(), id: z.number() }).nullable().optional(),
    productPackaging: z.object({ "@id": z.string(), id: z.number() }).nullable().optional(),
    stage: z.union([
        z.literal("ingredient_to_processing_site"),
        z.literal("packaging_to_processing_site"),
        z.literal("product_to_storage"),
        z.literal("storage_to_retail"),
        z.literal("retail_to_consumer"),
        z.literal("custom"),
    ]),
    transportCircuit: ProductTransportCircuitSchema,
});

export type ProductDistribution = {
    "@id": IRI;
    id: number;
    name: string;
    distributionReferenceId: number;
};

export const ProductDistributionSchema: z.ZodSchema<ProductDistribution> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    distributionReferenceId: z.number(),
});

export type Product = {
    "@id": string;
    id: number;
    ownedBy?: {
        "@id": IRI;
        id: number;
        name: string;
    } | null;
    title: string;
    clientId?: string;
    brand?: Brand;
    productReferenceId?: number;
    netWeight?: number;
    productLine?: ProductLine;
    tags?: Record<string, string[]>; // Format objet groupé par type de tag
    jsonAttributes?: Record<string, any>;
    productIngredients?: ProductIngredient[];
    productPackagings?: ProductPackaging[];
    productTransformations?: ProductTransformation[];
    productTransports?: ProductTransport[];
    shares?: ProductCompany[];
    productConsumptions?: ProductConsumption[];
    photos?: ProductPhoto[];
    distributionReferenceId?: number;
    distribution?: ProductDistribution | null;
    ssqId?: number;
    isIntermediate?: boolean;
    organic?: boolean;
    canModify: boolean;
    canDelete: boolean;
    olapScores: OlapScoresResult;
    olapPrevScores?: OlapScoresResult;
    prevCampaignName?: string;
};

type ProductWithAttributes = Product & { [key: string]: Record<string, string | number | boolean> };

// Schéma pour accepter soit un objet soit un tableau vide (API Platform)
const ProductTagsSchema = z
    .union([
        z.record(z.string(), z.array(z.string())), // Format objet standard
        z.array(z.never()).length(0), // Tableau vide uniquement
    ])
    .transform((value): Record<string, string[]> => {
        // Convertir tableau vide en objet vide pour cohérence
        return Array.isArray(value) ? {} : value;
    }) as z.ZodType<Record<string, string[]>>;

// Schéma pour accepter soit un objet soit un tableau vide (API Platform)
const JsonAttributesSchema = z
    .union([
        z.record(z.string(), z.any()), // Format objet standard
        z.array(z.never()).length(0), // Tableau vide uniquement
    ])
    .transform((value): Record<string, any> => {
        // Convertir tableau vide en objet vide pour cohérence
        return Array.isArray(value) ? {} : value;
    }) as z.ZodType<Record<string, any>>;

export const ProductSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    ownedBy: z
        .object({
            "@id": z.string(),
            id: z.number(),
            name: z.string(),
        })
        .optional(),
    title: z.string(),
    clientId: z.string().optional(),
    brand: BrandSchema.optional(),
    productReferenceId: z.number().optional(),
    netWeight: z.number().optional(),
    productLine: ProductLineZodSchema.optional(),
    jsonAttributes: JsonAttributesSchema.optional(), // Accepte objet ou tableau vide
    tags: ProductTagsSchema.optional(), // Accepte objet ou tableau vide
    productIngredients: ProductIngredientSchema.array().optional(),
    productPackagings: ProductPackagingSchema.array().optional(),
    productTransformations: ProductTransformationSchema.array().optional(),
    productTransports: ProductTransportSchema.array().optional(),
    shares: ProductCompanySchema.array().optional(),
    productConsumptions: ProductConsumptionSchema.array().optional(),
    photos: ProductPhotoSchema.array().optional(),
    ssqId: z.number().optional(),
    isIntermediate: z.boolean().optional(),
    organic: z.boolean().optional(),
    distributionReferenceId: z.number().optional(),
    distribution: ProductDistributionSchema.nullable().optional(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    // GET /products/{id} does not embed scores (unlike list); they come from
    // /products/{id}/campaign/{campaignId}/scores via useGetProductScores.
    olapScores: OlapScoresResultSchema.default({}),
    olapPrevScores: OlapScoresResultSchema.optional(),
    prevCampaignName: z.string().optional(),
});
